import { capitalize } from "lodash";
import { PostFormState } from "../../../../shared/types/post";
import { ContextMenu } from "./context-menu";
import { Tooltip } from "@dialogueconsulting/sked-ui";
import SkedIcon from "../../../../ui/SkedIcon/SkedIcon";

export const PostMiniCardBase = ({
  card,
  onClick,
  onClose,
  onDuplicate,
  showDuplicate,
}: {
  card: Card;
  onClick: () => void;
  onClose: () => void;
  onDuplicate: () => void;
  showDuplicate?: boolean;
}) => {
  return (
    <div className="relative" onClick={onClick}>
      <div
        className={`flex bg-white rounded-l-[8px]  relative group ${card.isSelected ? "drop-shadow-md" : "rounded-r-[8px]"}`}
      >
        <CardPreview content={card.media} />
        <div className="flex flex-col flex-1 px-[8px] py-[4px]">
          <PostTypeInfo postType={card.postType} />

          <div className="flex items-center mt-auto">
            <PostStatus status={card.postStatus} />
            <FormState card={card} />
          </div>
          <ContextMenu
            isSelected={!!card.isSelected}
            onClose={onClose}
            onDuplicate={onDuplicate}
            allowDuplicate={!!showDuplicate}
          />
        </div>
      </div>
      <div
        className={`absolute top-0 right-[-12px] bottom-0  w-[12px] ${card.isSelected ? "bg-white" : "bg-transparent"}`}
      />
    </div>
  );
};
function PostStatus({ status }: { status: Card["postStatus"] | undefined }) {
  status = status || {
    color: "var(--color-neutral-300)",
    text: "Work in progress",
  };
  return (
    <div className="flex gap-[3px] items-center min-w-0">
      <div
        className="shrink-0 w-[10px] h-[10px] rounded-[2px]"
        style={{
          backgroundColor: status.color,
        }}
      />
      <span className="text-[10px] text-neutral-800 truncate mr-[4px]">
        {status.text}
      </span>
    </div>
  );
}

function PostTypeInfo({ postType }: { postType: CardPostType | undefined }) {
  if (postType?.type === "archived") {
    return (
      <div className="self-start flex px-[6px] py-[3px] text-[10px] text-neutral-800 bg-neutral-25 rounded-[16px] items-center">
        <i className="fa-regular fa-box-archive text-[12px] pr-[4px]" />
        Archived
      </div>
    );
  }
  if (postType?.type === "queued") {
    return (
      <div className="self-start flex px-[6px] py-[3px] text-[10px] text-blue-500 bg-neutral-25 rounded-[16px] items-center">
        <i className="fa-regular fa-layer-group text-[12px] pr-[4px]" />
        Queue
      </div>
    );
  }
  if (!postType || postType.type === "draft") {
    return (
      <div className="self-start flex px-[6px] py-[3px] text-[10px] text-neutral-800 bg-neutral-25 rounded-[16px] items-center">
        <i className="fa-regular fa-file text-[12px] pr-[4px]" />
        {capitalize(postType?.type || "draft")}
      </div>
    );
  }
  if (postType.type === "scheduled") {
    if (postType.isNow) {
      return (
        <div className="self-start flex px-[6px] py-[3px] text-[10px] text-blue-500 bg-blue-25 rounded-[16px] items-center">
          <i className="fa-regular fa-paper-plane-top text-[12px] pr-[4px]" />
          ASAP
        </div>
      );
    }
    return (
      <div className="self-start flex px-[6px] py-[3px] text-[10px] text-blue-500 bg-blue-25 rounded-[16px] items-center">
        <i className="fa-regular fa-alarm-clock text-[12px] pr-[4px]" />
        {postType.isNow ? "Post ASAP" : postType.scheduledTime}
      </div>
    );
  }
  return null;
}

function FormState({ card }: { card: Card }) {
  const tooltipContentStyle: React.CSSProperties = {
    fontSize: 12,
  };
  return (
    <div className="flex ml-auto shrink-0 gap-[2px]">
      {(() => {
        const icons: React.ReactNode[] = [];

        if (card.errorCount) {
          icons.push(
            <Tooltip
              text={`${card.errorCount} error${card.errorCount === 1 ? "" : "s"}`}
              contentBackgroundStyle={tooltipContentStyle}
            >
              <div className="flex w-[10px] h-[10px]  bg-red-500 justify-center items-center rounded-[2px]">
                <i
                  key="error"
                  className="fa-solid fa-xmark text-[6px] text-white"
                />
              </div>
            </Tooltip>
          );
        }
        if (card.warningCount) {
          icons.push(
            <Tooltip
              text={`${card.warningCount} warning${card.warningCount === 1 ? "" : "s"}`}
              contentBackgroundStyle={tooltipContentStyle}
            >
              <div className="flex w-[10px] h-[10px]  bg-yellow-500 justify-center items-center rounded-[2px]">
                <i
                  key="warning"
                  className="fa-solid fa-exclamation text-[7px] text-white"
                />
              </div>
            </Tooltip>
          );
        }
        if (card.editState === "editing") {
          icons.push(
            <Tooltip
              text="Unsaved"
              contentBackgroundStyle={tooltipContentStyle}
            >
              <div className="flex w-[10px] h-[10px]  bg-blue-500 justify-center items-center rounded-[2px]">
                <i
                  key="editing"
                  className="fa-solid fa-pencil text-[6px] text-white"
                />
              </div>
            </Tooltip>
          );
        } else {
          icons.push(
            <Tooltip text="Saved" contentBackgroundStyle={tooltipContentStyle}>
              <div className="flex w-[10px] h-[10px]  bg-green-500 justify-center items-center rounded-[2px]">
                <i
                  key="saved"
                  className="fa-solid fa-check text-[6px] text-white"
                />
              </div>
            </Tooltip>
          );
        }

        return icons;
      })()}
    </div>
  );
}

function CardPreview({ content }: { content: CardMedia }) {
  const renderImgSection = (src: string, icon: React.ReactNode) => {
    return (
      <div className="flex-shrink-0  w-[44px] h-[55px]  bg-neutral-300 rounded-l-[8px] relative">
        <img
          src={src}
          alt=""
          className="object-cover w-full h-full rounded-l-[8px]"
        />
        <div className="flex absolute size-[20px] top-[4px] right-[4px] bg-black/40 rounded-full items-center justify-center">
          {icon}
        </div>
      </div>
    );
  };
  const emptyArea = (
    <div className="flex-shrink-0  w-[44px] h-[55px] p-[4px]  bg-neutral-800 rounded-l-[8px] flex items-center justify-center">
      <i className="fa-regular fa-image-slash text-white text-[16px]" />
    </div>
  );
  const skedIcon = (icon: string) => {
    return (
      <SkedIcon
        icon={icon}
        size="12"
        color="white"
        className="cursor-default"
      />
    );
  };
  switch (content.type) {
    case "text": {
      if (!content.text?.trim()) {
        return emptyArea;
      }

      return (
        <div className="flex-shrink-0  w-[44px] h-[55px] p-[4px] text-white bg-blue-300 rounded-l-[8px]">
          <p className="text-[8px] line-clamp-4 leading-[10px] wrap-break-word text-white">
            {content.text}
          </p>
        </div>
      );
    }
    case "image": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-image text-white text-[12px]" />
      );
    }
    case "reel": {
      return renderImgSection(content.thumbnail, skedIcon("icon-post-reel"));
    }
    case "video": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-video text-white text-[12px]" />
      );
    }
    case "carousel": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-rectangle-vertical-history text-white text-[12px]" />
      );
    }
    case "document": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-file-pdf text-white text-[12px]" />
      );
    }
    case "unsupported": {
      return emptyArea;
    }
    case "story": {
      if (!content.thumbnail) {
        return emptyArea;
      }

      return renderImgSection(content.thumbnail, skedIcon("icon-post-story"));
    }
    default: {
      return (
        <div className="flex-shrink-0 w-[44px] h-[55px] p-[4px] bg-neutral-300 rounded-l-[8px]">
          <p className="text-[8px] line-clamp-4 leading-[10px] wrap-break-word">
            Unsupported content
          </p>
        </div>
      );
    }
  }
}

interface TextCardMedia {
  type: "text";
  text: string;
}

interface CommonCardMedia {
  type: "video" | "image" | "carousel" | "document" | "reel" | "igtv" | "story";
  thumbnail: string;
}

interface UnsupportedCardMedia {
  type: "unsupported";
}

type CardMedia = TextCardMedia | CommonCardMedia | UnsupportedCardMedia;

interface DraftPostType {
  type: "draft";
}
interface QueuePostType {
  type: "queued";
}
interface ArchivedPostType {
  type: "archived";
}

interface ScheduledPostType {
  type: "scheduled";
  scheduledTime: string;
  isNow: boolean;
}

type CardPostType =
  | DraftPostType
  | ScheduledPostType
  | QueuePostType
  | ArchivedPostType;

export interface Card {
  postId: string;
  postType?: CardPostType;
  media: CardMedia;
  editState?: "saved" | "editing";
  errorCount?: number;
  warningCount?: number;
  postStatus?: {
    color: string;
    text: string;
  };
  isSelected?: boolean;
  clusterId?: string;
  form: PostFormState;
}
